/**
 * Store Code Change Event Emitter
 *
 * Centralized event system for handling store code changes when
 * user selects a different region. Allows modules to react to
 * store code changes without direct coupling.
 *
 * Mirrors mobile's module-core/src/modules/contracts/store-code-emitter.ts
 *
 * Web-specific design:
 * - Cookie is the SINGLE SOURCE OF TRUTH for store code
 *   (read by Next.js middleware, Apollo Client, SSR)
 * - Cookie adapter is injectable so the app layer can use `js-cookie`
 *   while module-core stays free of browser-specific dependencies
 * - globalStore$ keeps an in-memory copy for reactive Legend-State access
 *
 * USAGE:
 * ```typescript
 * // 1. App bootstrap — inject js-cookie adapter (in _app.tsx or similar)
 * import Cookies from 'js-cookie';
 * storeCodeEmitter.setCookieAdapter({
 *     get: (name) => Cookies.get(name) ?? null,
 *     set: (name, value, days) => Cookies.set(name, value, { expires: days, path: '/' }),
 *     remove: (name) => Cookies.remove(name, { path: '/' }),
 * });
 *
 * // 2. Subscribe to store code changes (e.g., in module-quote)
 * storeCodeEmitter.onStoreCodeChanged('quote', async (payload) => {
 *     cartStoreAPI.actions.resetCart();
 * });
 *
 * // 3. Emit store code change (in region-selector)
 * await storeCodeEmitter.changeStoreCode(newRegion);
 * ```
 */

import EventEmitter from './event-emitter';
import type {
    StoreCodeEventMap,
    StoreCodeChangePayload,
    BeforeStoreCodeChangePayload,
    StoreCodeChangeHandler,
    BeforeStoreCodeChangeHandler,
    StoreCodeChangeResult,
} from './store-code-emitter.types';
import type { StoreRegion } from '../directories/constants/region.constants';
import { globalStore$ } from '../stores/global.store';
import { RegionsStores } from '../../static/regions-stores';

const DEFAULT_STORE_CODE = 'hn_gen_two';
const STORE_COOKIE_KEY = 'store';
const COOKIE_EXPIRY_DAYS = 365;

// ============================================================================
// Cookie Adapter
// ============================================================================

/**
 * Cookie adapter interface — allows app layer to inject `js-cookie` or
 * any other cookie library without module-core depending on it.
 */
export interface CookieAdapter {
    get: (name: string) => string | null | undefined;
    set: (name: string, value: string, days: number) => void;
    remove: (name: string) => void;
}

/**
 * Default cookie adapter using raw document.cookie (fallback).
 * Works without external dependencies, but less robust than js-cookie
 * (no automatic encoding edge-cases, no path normalization, etc.)
 */
const defaultCookieAdapter: CookieAdapter = {
    get(name: string): string | null {
        if (typeof document === 'undefined') return null;
        const match = document.cookie.match(
            new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
        );
        return match ? decodeURIComponent(match[1]) : null;
    },
    set(name: string, value: string, days: number): void {
        if (typeof document === 'undefined') return;
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    },
    remove(name: string): void {
        if (typeof document === 'undefined') return;
        document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    },
};

// ============================================================================
// Region → Store Code Mapping
// ============================================================================

const buildRegionToStoreCodeMap = (): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const region of RegionsStores) {
        if (region.region_code && region.store_code) {
            map[region.region_code] = region.store_code;
        }
    }
    return map;
};

const buildRegionIdToStoreCodeMap = (): Record<number, string> => {
    const map: Record<number, string> = {};
    for (const region of RegionsStores) {
        if (region.region_id && region.store_code) {
            map[region.region_id] = region.store_code;
        }
    }
    return map;
};

const REGION_TO_STORE_CODE_MAP: Record<string, string> = buildRegionToStoreCodeMap();
const REGION_ID_TO_STORE_CODE_MAP: Record<number, string> = buildRegionIdToStoreCodeMap();

// ============================================================================
// StoreCodeEmitter
// ============================================================================

class StoreCodeEmitter {
    private emitter = new EventEmitter<StoreCodeEventMap>();
    private cookieAdapter: CookieAdapter = defaultCookieAdapter;

    private handlers: Map<
        string,
        {
            onStoreCodeChanged?: StoreCodeChangeHandler;
            onBeforeStoreCodeChange?: BeforeStoreCodeChangeHandler;
        }
    > = new Map();

    // ========================================================================
    // Cookie Adapter Management
    // ========================================================================

    /**
     * Inject a custom cookie adapter (e.g. js-cookie).
     * Should be called once at app startup.
     *
     * @example
     * ```typescript
     * import Cookies from 'js-cookie';
     * storeCodeEmitter.setCookieAdapter({
     *     get: (name) => Cookies.get(name) ?? null,
     *     set: (name, value, days) => Cookies.set(name, value, { expires: days, path: '/' }),
     *     remove: (name) => Cookies.remove(name, { path: '/' }),
     * });
     * ```
     */
    public setCookieAdapter(adapter: CookieAdapter): void {
        this.cookieAdapter = adapter;
        console.log('[StoreCodeEmitter] Custom cookie adapter injected');
    }

    // ========================================================================
    // Region → Store Code Resolution
    // ========================================================================

    /**
     * Get the store code for a region.
     * Supports lookup by region code (e.g., 'VN-63') or region ID (e.g., 619).
     */
    public getStoreCodeForRegion(region: StoreRegion): string {
        if (region.code && REGION_TO_STORE_CODE_MAP[region.code]) {
            return REGION_TO_STORE_CODE_MAP[region.code];
        }
        if (region.id && REGION_ID_TO_STORE_CODE_MAP[region.id]) {
            return REGION_ID_TO_STORE_CODE_MAP[region.id];
        }
        return DEFAULT_STORE_CODE;
    }

    // ========================================================================
    // Store Code Read / Write  (cookie is the source of truth)
    // ========================================================================

    /**
     * Get the current store code.
     * Reads from cookie first (source of truth for SSR/middleware),
     * then falls back to globalStore$ (for reactive access).
     */
    public getCurrentStoreCode(): string {
        const fromCookie = this.cookieAdapter.get(STORE_COOKIE_KEY);
        if (fromCookie) return fromCookie;

        const fromStore = globalStore$.storeCode.get();
        if (fromStore && fromStore !== 'default') return fromStore;

        return DEFAULT_STORE_CODE;
    }

    /**
     * Persist store code to cookie + globalStore$.
     * Private — only called inside changeStoreCode().
     */
    private persistStoreCode(storeCode: string): void {
        // Cookie — primary persistence (read by middleware, Apollo)
        this.cookieAdapter.set(STORE_COOKIE_KEY, storeCode, COOKIE_EXPIRY_DAYS);

        // globalStore$ — reactive in-memory copy
        globalStore$.storeCode.set(storeCode);
    }

    /**
     * Check if store code needs to change for a new region
     */
    public needsStoreCodeChange(newRegion: StoreRegion): boolean {
        const currentStoreCode = this.getCurrentStoreCode();
        const newStoreCode = this.getStoreCodeForRegion(newRegion);
        return currentStoreCode !== newStoreCode;
    }

    // ========================================================================
    // Event Subscription
    // ========================================================================

    public onStoreCodeChanged(moduleId: string, handler: StoreCodeChangeHandler): void {
        const existing = this.handlers.get(moduleId) ?? {};
        this.handlers.set(moduleId, { ...existing, onStoreCodeChanged: handler });
        this.emitter.on('storeCodeChanged', handler);
        console.log(`[StoreCodeEmitter] Registered storeCodeChanged handler for module: ${moduleId}`);
    }

    public onBeforeStoreCodeChange(moduleId: string, handler: BeforeStoreCodeChangeHandler): void {
        const existing = this.handlers.get(moduleId) ?? {};
        this.handlers.set(moduleId, { ...existing, onBeforeStoreCodeChange: handler });
        this.emitter.on('beforeStoreCodeChange', handler);
        console.log(`[StoreCodeEmitter] Registered beforeStoreCodeChange handler for module: ${moduleId}`);
    }

    public unregister(moduleId: string): void {
        const handlerInfo = this.handlers.get(moduleId);
        if (handlerInfo) {
            if (handlerInfo.onStoreCodeChanged) {
                this.emitter.off('storeCodeChanged', handlerInfo.onStoreCodeChanged);
            }
            if (handlerInfo.onBeforeStoreCodeChange) {
                this.emitter.off('beforeStoreCodeChange', handlerInfo.onBeforeStoreCodeChange);
            }
            this.handlers.delete(moduleId);
            console.log(`[StoreCodeEmitter] Unregistered handlers for module: ${moduleId}`);
        }
    }

    public getRegisteredModules(): string[] {
        return Array.from(this.handlers.keys());
    }

    // ========================================================================
    // Core: Change Store Code
    // ========================================================================

    /**
     * Change the store code based on new region selection.
     *
     * Flow:
     * 1. Emit 'beforeStoreCodeChange' (modules can save state)
     * 2. Write store code to **cookie** (source of truth for SSR/middleware/Apollo)
     * 3. Write store code to **globalStore$** (reactive in-memory copy)
     * 4. Write currentRegion to **globalStore$**
     * 5. Emit 'storeCodeChanged' (modules can reload data)
     */
    public async changeStoreCode(newRegion: StoreRegion): Promise<StoreCodeChangeResult> {
        const previousStoreCode = this.getCurrentStoreCode();
        const previousRegion = globalStore$.currentRegion.get() ?? null;
        const newStoreCode = this.getStoreCodeForRegion(newRegion);

        // If store code is the same, just update region and return
        if (previousStoreCode === newStoreCode) {
            globalStore$.currentRegion.set(newRegion);
            console.log(`[StoreCodeEmitter] Store code unchanged (${newStoreCode}), only region updated`);
            return {
                success: true,
                previousStoreCode,
                newStoreCode,
            };
        }

        try {
            console.log(`[StoreCodeEmitter] Changing store code from ${previousStoreCode} to ${newStoreCode}`);

            // 1. Emit before change event
            const beforePayload: BeforeStoreCodeChangePayload = {
                currentStoreCode: previousStoreCode,
                newStoreCode,
                currentRegion: previousRegion,
                newRegion,
            };
            this.emitter.emit('beforeStoreCodeChange', beforePayload);

            // 2+3. Persist store code (cookie + globalStore$)
            this.persistStoreCode(newStoreCode);

            // 4. Update region in global store
            globalStore$.currentRegion.set(newRegion);

            // 5. Emit store code changed event and AWAIT all handlers
            //    This ensures async cleanup (clearing cart data, session, etc.)
            //    completes before the caller proceeds (e.g., page reload).
            const changedPayload: StoreCodeChangePayload = {
                previousStoreCode,
                newStoreCode,
                previousRegion,
                newRegion,
            };
            await this.emitter.emitAsync('storeCodeChanged', changedPayload);

            console.log(`[StoreCodeEmitter] Store code change completed successfully`);

            return {
                success: true,
                previousStoreCode,
                newStoreCode,
            };
        } catch (error) {
            console.error('[StoreCodeEmitter] Error changing store code:', error);
            return {
                success: false,
                previousStoreCode,
                newStoreCode,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    }

    // ========================================================================
    // Utilities
    // ========================================================================

    public getStoreCodeMapping(): Readonly<Record<string, string>> {
        return { ...REGION_TO_STORE_CODE_MAP };
    }

    /** Clear all handlers (useful for testing) */
    public clear(): void {
        this.handlers.forEach((_, moduleId) => {
            this.unregister(moduleId);
        });
        console.log('[StoreCodeEmitter] Cleared all handlers');
    }
}

// Export singleton instance
export const storeCodeEmitter = new StoreCodeEmitter();

// Export class for testing
export { StoreCodeEmitter };

// Export constants
export { DEFAULT_STORE_CODE };
